import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { downloadBlobFile, captureError } from '@clatter/platform';
import { buttonVariants } from '@clatter/ui';
import { useUpload } from './useUpload';
import { useAssetDownload } from './useAssetDownload';
import {
  useCreateAssetMutation,
  useDeleteAssetByIdMutation,
  useDeleteFolderByIdMutation,
  useGenerateZipBuildJobMutation,
  useGetPresentationBuildMutation,
  useGetSlideDownloadMutation,
  useLazyGetPresentationQuery,
  useUpdateAssetMutation,
  useUpdateFolderMutation,
  useUpdatePresentationMutation,
  useDeletePresentationMutation,
  useUpdatePresentationThemeMutation,
} from '../services';
import { baseApi } from '../services/baseApi';
import { addNotification, addLongPullUIItem } from '../store';
import {
  FolderManagerAction,
  FolderManagerActionTypes,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
} from '../interfaces';
import { ManageThemeDialog } from '../components/ManageThemeDialog/ManageThemeDialog';
import { ConfirmationDialog } from '../components/ConfirmationDialog/ConfirmationDialog';
import { AssetVersionManager } from '../components/AssetVersionManager/AssetVersionManager';
import { FolderManagerDialog } from '../components/FolderManager/Components/FolderManagerDialog';
import { FolderManagerMoveDialog } from '../components/FolderManager/Components/FolderManagerMoveDialog';
import { FolderManagerShareDialog } from '../components/FolderManager/Components/FolderManagerShareDialog';
import { FolderManagerSlidesDialog } from '../components/FolderManager/Components/FolderManagerSlidesDialog';

const UPLOAD_FILES_LIMIT = 100;

interface UseFolderManagerActionsOptions {
  slug?: string;
  selectedFolderId?: number;
  selectedFolderData?: any;
  rootFolder?: any;
  isSlidesFolder?: boolean;
  customActionHandlers?: Partial<
    Record<FolderManagerActionTypes, (action: FolderManagerAction) => void | Promise<void>>
  >;
  onActionSuccess?: (actionType: FolderManagerActionTypes) => void;
  onActionError?: (actionType: FolderManagerActionTypes, error: any) => void;
}

interface IRenameDialogState {
  opened: boolean;
  actionType: FolderManagerActionTypes | null;
  editedItem: any;
}

interface IConfirmationDialogState {
  opened: boolean;
  title: string;
  content: React.ReactNode;
  buttons?: any[];
}

interface IMoveDialogState {
  opened: boolean;
  rootFolder: any;
  selectedItems: FolderManagerItem[];
}

interface IManageThemeDialogState {
  opened: boolean;
  themeItem: any;
}

interface FolderManagerItem {
  id: number;
  name?: string;
  type?: string;
  parentId?: number;
}

const shareDialogInitialState = {
  opened: false,
  selectedItems: [],
};

const initialRenameDialogState: IRenameDialogState = {
  opened: false,
  actionType: null,
  editedItem: null,
};

const initialConfirmationDialogState: IConfirmationDialogState = {
  opened: false,
  title: 'Confirm',
  content: 'Confirm action',
};

const initialMoveDialogState: IMoveDialogState = {
  opened: false,
  rootFolder: null,
  selectedItems: [],
};

const initialManageThemeDialogState: IManageThemeDialogState = {
  opened: false,
  themeItem: null,
};

const initialSlidesDialogState: FolderManagerSlidesDialogState = {
  title: '',
  opened: false,
  jobId: null,
  filesToUpload: null,
  folderId: 0,
};

export const useFolderManagerActions = ({
  slug,
  selectedFolderId,
  selectedFolderData,
  rootFolder,
  isSlidesFolder,
  customActionHandlers = {},
  onActionSuccess,
  onActionError,
}: UseFolderManagerActionsOptions) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { downloadAssetById } = useAssetDownload();
  const { uploadFiles } = useUpload();

  const [moveDialogState, setMoveDialogState] = useState<any>(initialMoveDialogState);
  const [slidesDialogState, setSlidesDialogState] = useState<FolderManagerSlidesDialogState>(initialSlidesDialogState);
  const [shareDialogState, setShareDialogState] = useState(shareDialogInitialState);
  const [renameDialogState, setRenameDialogState] = useState(initialRenameDialogState);
  const [confirmationDialogState, setConfirmationDialogState] = useState(initialConfirmationDialogState);
  const [manageThemeDialogState, setManageThemeDialogState] = useState(initialManageThemeDialogState);

  const [createAsset] = useCreateAssetMutation();
  const [updateAsset] = useUpdateAssetMutation();
  const [updateFolder] = useUpdateFolderMutation();
  const [updatePresentation] = useUpdatePresentationMutation();
  const [deleteAsset] = useDeleteAssetByIdMutation();
  const [deleteFolder] = useDeleteFolderByIdMutation();
  const [deletePresentation] = useDeletePresentationMutation();
  const [generateZipBuildJob] = useGenerateZipBuildJobMutation();
  const [updatePresentationTheme] = useUpdatePresentationThemeMutation();

  const [downloadPresentationBuild] = useGetPresentationBuildMutation();
  const [downloadSlide] = useGetSlideDownloadMutation();
  const [getPresentation] = useLazyGetPresentationQuery();

  const handleRestore = useCallback(
    async (payload: any) => {
      const itemsToRestore = payload?.selectedItems ? payload.selectedItems : [payload];
      let restorePromises: Promise<any>[] = [];

      if (
        window.confirm(
          `Restore ${
            itemsToRestore.length > 1 ? itemsToRestore?.length + ' items ' : itemsToRestore?.[0]?.name
          }  from trash?`,
        )
      ) {
        for (const itemToDelete of itemsToRestore) {
          if (itemToDelete.type === FolderManagerItemTypes.FOLDER) {
            restorePromises.push(
              updateFolder({
                folderId: itemToDelete.id,
                data: { restore_from_trash: true },
              }),
            );
          } else if (itemToDelete.type === FolderManagerItemTypes.PRESENTATION) {
            restorePromises.push(
              updatePresentation({
                presentationId: itemToDelete.id,
                data: { restore_from_trash: true },
              }),
            );
          } else {
            restorePromises.push(
              updateAsset({
                assetId: itemToDelete.id,
                data: { restore_from_trash: true },
              }),
            );
          }
        }
      }

      if (restorePromises?.length) {
        try {
          await Promise.all(restorePromises);
          onActionSuccess?.(FolderManagerActionTypes.RESTORE);
        } catch (error: any) {
          captureError(error, { message: 'Error restoring item(s)', component: 'useFolderManagerActions' });
          onActionError?.(FolderManagerActionTypes.RESTORE, error);
        }
      }
    },
    [updateFolder, updateAsset, updatePresentation, onActionSuccess, onActionError],
  );

  const handleAction = useCallback(
    async ({ actionType, payload, isMultiSelect }: FolderManagerAction) => {
      // Check if there's a custom handler for this action
      if (customActionHandlers[actionType]) {
        await customActionHandlers[actionType]!({ actionType, payload, isMultiSelect });
        return;
      }

      const { row, selectedItems } = payload || {};

      switch (actionType) {
        case FolderManagerActionTypes.RENAME:
          setRenameDialogState({
            opened: true,
            editedItem: payload,
            actionType: actionType,
          });
          break;

        case FolderManagerActionTypes.EDIT:
          if (payload?.type === FolderManagerItemTypes.PRESENTATION) {
            navigate(`/presentations/${payload.id}`);
            break;
          }
          break;

        case FolderManagerActionTypes.SHARE:
          setShareDialogState({
            opened: true,
            selectedItems: isMultiSelect ? selectedItems : [row],
          });
          break;

        case FolderManagerActionTypes.RESTORE:
          await handleRestore(payload);
          break;

        case FolderManagerActionTypes.DELETE:
          const itemsToDelete: FolderManagerItem[] = payload?.selectedItems ? payload?.selectedItems : [payload];
          let deletePromises: Promise<any>[] = [];

          // Special case: slide items cannot be trashed individually — the parent pptx asset must be trashed instead.
          // A single pptx deck may contain many slides, so trashing one slide trashes the whole deck.
          const hasOnlySlides =
            itemsToDelete.length > 0 && itemsToDelete.every((item) => item.type === FolderManagerItemTypes.SLIDE);

          if (hasOnlySlides && slug !== 'trash') {
            // Collect unique parent pptx assets (multiple selected slides may share the same parent)
            const parentPptxMap = new Map<number, any>();
            for (const slideItem of itemsToDelete) {
              const parentPptx = (slideItem as any).parent;
              if (parentPptx?.id) {
                parentPptxMap.set(parentPptx.id, parentPptx);
              }
            }

            const parentPptxItems = Array.from(parentPptxMap.values());

            if (parentPptxItems.length === 0) {
              dispatch(
                addNotification({
                  type: 'error',
                  message: "Couldn't move the selected slides to trash — parent deck not found.",
                }),
              );
              return;
            }

            const totalSlideCount = parentPptxItems.reduce((sum, pptx) => sum + (pptx.slides?.length ?? 0), 0);
            const deckNames = parentPptxItems.map((p) => `"${p.displayName || p.name}"`).join(', ');

            const confirmMsg =
              parentPptxItems.length === 1
                ? `Move deck ${deckNames} to trash? All ${totalSlideCount} slide(s) in this deck will also be moved.`
                : `Move ${parentPptxItems.length} deck(s) (${deckNames}) to trash? All ${totalSlideCount} slide(s) in these decks will also be moved.`;

            if (!window.confirm(confirmMsg)) return;

            try {
              await Promise.all(
                parentPptxItems.map((parent) => updateAsset({ assetId: parent.id, data: { move_to_trash: true } })),
              );
              dispatch(
                addNotification({
                  type: 'success',
                  message: `Successfully moved ${parentPptxItems.length} deck(s) to trash!`,
                }),
              );
              onActionSuccess?.(FolderManagerActionTypes.DELETE);
            } catch (error) {
              const msg = "Couldn't move the deck(s) to trash. Please try again.";
              captureError(error, { message: msg, component: 'useFolderManagerActions' });
              dispatch(addNotification({ type: 'error', message: msg }));
              onActionError?.(FolderManagerActionTypes.DELETE, error);
            }
            return;
          }

          // move to trash
          if (slug !== 'trash') {
            if (
              window.confirm(
                `Move ${
                  itemsToDelete.length > 1 ? itemsToDelete?.length + ' items' : itemsToDelete?.[0]?.name
                } to trash?`,
              )
            ) {
              try {
                for (const itemToDelete of itemsToDelete) {
                  if (itemToDelete?.type === FolderManagerItemTypes.PRESENTATION) {
                    deletePromises.push(
                      updatePresentation({
                        presentationId: itemToDelete.id,
                        data: { move_to_trash: true },
                        currentFolderId: itemToDelete.parentId,
                      }),
                    );
                  } else if (itemToDelete?.type === FolderManagerItemTypes.FOLDER) {
                    deletePromises.push(
                      updateFolder({
                        folderId: itemToDelete.id,
                        data: { move_to_trash: true },
                      }),
                    );
                  } else {
                    deletePromises.push(
                      updateAsset({
                        assetId: itemToDelete.id,
                        data: { move_to_trash: true },
                      }),
                    );
                  }
                }

                await Promise.all(deletePromises);

                dispatch(
                  addNotification({
                    type: 'success',
                    message: `Successfully moved ${itemsToDelete.length} item(s) to trash!`,
                  }),
                );
                onActionSuccess?.(FolderManagerActionTypes.DELETE);
              } catch (error) {
                const msg = `Error moving item(s) to trash!`;
                captureError(error, { message: msg, component: 'useFolderManagerActions' });

                dispatch(
                  addNotification({
                    type: 'error',
                    message: msg,
                  }),
                );
                onActionError?.(FolderManagerActionTypes.DELETE, error);
              }
            }
          }

          // permanent delete
          if (slug === 'trash') {
            if (
              window.confirm(
                `Delete ${
                  itemsToDelete.length > 1 ? itemsToDelete?.length + ' items' : itemsToDelete?.[0]?.name
                } permanently? This can not be undone!`,
              )
            ) {
              for (const itemToDelete of itemsToDelete) {
                if (itemToDelete?.type === FolderManagerItemTypes.FOLDER) {
                  deletePromises.push(deleteFolder({ folderId: itemToDelete?.id }));
                } else if (itemToDelete?.type === FolderManagerItemTypes.PRESENTATION) {
                  deletePromises.push(deletePresentation({ presentationId: itemToDelete?.id }));
                } else {
                  deletePromises.push(deleteAsset({ assetId: itemToDelete?.id }));
                }
              }

              if (deletePromises?.length) {
                try {
                  await Promise.all(deletePromises);
                  onActionSuccess?.(FolderManagerActionTypes.DELETE);
                } catch (error: any) {
                  captureError(error, { message: 'Error deleting item', component: 'useFolderManagerActions' });
                  onActionError?.(FolderManagerActionTypes.DELETE, error);
                }
              }
            }
          }
          break;

        case FolderManagerActionTypes.DOWNLOAD:
          // For slide items, download the parent pptx deck asset instead
          if (payload?.type === FolderManagerItemTypes.SLIDE) {
            const parentDeckId = (payload as any).parent?.id;
            if (!parentDeckId) {
              dispatch(
                addNotification({ type: 'error', message: "Couldn't download this slide — parent deck not found." }),
              );
              break;
            }

            await downloadAssetById({ assetId: parentDeckId });

            onActionSuccess?.(FolderManagerActionTypes.DOWNLOAD);
            break;
          }

          const { id: assetId, type: itemType } = payload || {};

          if (itemType === FolderManagerItemTypes.PRESENTATION) {
            if (!assetId || Number.isNaN(assetId)) {
              const msg = 'Error downloading presentation: missing or invalid ID!';
              captureError(new Error(msg), { component: 'useFolderManagerActions', skipConsole: true });
              dispatch(
                addNotification({
                  type: 'error',
                  message: msg,
                }),
              );
              onActionError?.(FolderManagerActionTypes.DOWNLOAD, new Error(msg));
              break;
            }

            try {
              // First, fetch the presentation to check if it has slides
              const presentationData = await getPresentation({ presentationId: Number(assetId) }).unwrap();

              // Check if presentation has slides
              if (!presentationData?.slides || presentationData.slides.length === 0) {
                const msg = 'Cannot download empty presentation. Please add slides first.';
                dispatch(
                  addNotification({
                    type: 'error',
                    message: msg,
                  }),
                );
                onActionError?.(FolderManagerActionTypes.DOWNLOAD, new Error(msg));
                break;
              }

              // Show notification immediately before starting download
              dispatch(
                addNotification({
                  type: 'success',
                  message: "Preparing download… If it doesn't start automatically, check your browser settings.",
                }),
              );

              // Proceed with download
              const blob = await downloadPresentationBuild({ presentationId: Number(assetId) }).unwrap();
              if (blob) {
                const filename = `${payload?.name || 'presentation'}.pptx`;
                downloadBlobFile(blob, filename);
                onActionSuccess?.(FolderManagerActionTypes.DOWNLOAD);
              }
            } catch (error: any) {
              const msg = "Couldn't download the presentation. Please try again.";
              captureError(error, { message: 'Error downloading presentation', component: 'useFolderManagerActions' });
              dispatch(
                addNotification({
                  type: 'error',
                  message: msg,
                }),
              );
              onActionError?.(FolderManagerActionTypes.DOWNLOAD, error);
            }
            break;
          }

          if (selectedItems && selectedItems?.length) {
            const assets_ids = selectedItems.map((item: FolderManagerItem) => item.id);

            const response = await generateZipBuildJob({ data: { assets_ids: assets_ids } });

            //@ts-ignore
            const { ID: jobId } = response?.data?.data;

            if (!jobId) {
              const msg = 'Error generating zip. No job ID provided!';

              dispatch(
                addNotification({
                  type: 'error',
                  message: msg,
                }),
              );
              onActionError?.(FolderManagerActionTypes.DOWNLOAD, new Error(msg));
              return;
            }

            const newQueueItem = {
              id: jobId,
              isUploading: true,
              isLongpull: true,
              isCompleted: false,
              completedFiles: 0,
              totalFiles: 0,
              message: `Preparing files to zip...`,
              jobType: 'zip',
              type: 'running',
            };

            dispatch(addLongPullUIItem({ item: newQueueItem }));
            onActionSuccess?.(FolderManagerActionTypes.DOWNLOAD);
            return;
          }

          if (!assetId || Number.isNaN(assetId)) {
            captureError(new Error('Error downloading asset: missing or invalid ID!'), {
              component: 'useFolderManagerActions',
              skipConsole: true,
            });
            break;
          }

          await downloadAssetById({ assetId: assetId });
          onActionSuccess?.(FolderManagerActionTypes.DOWNLOAD);
          break;

        case FolderManagerActionTypes.DOWNLOAD_DECK:
          const parentAssetId = payload?.itemData?.id;

          if (!parentAssetId || Number.isNaN(parentAssetId)) {
            captureError(new Error('Error downloading deck: missing or invalid asset ID!'), {
              component: 'useFolderManagerActions',
              skipConsole: true,
            });
            break;
          }

          await downloadAssetById({ assetId: parentAssetId });
          onActionSuccess?.(FolderManagerActionTypes.DOWNLOAD_DECK);
          break;

        case FolderManagerActionTypes.SLIDE_DOWNLOAD: {
          const slideId = payload?.itemData?.slideData?.ID;

          // as for now api is returning invalid SlideID which is 0!!
          if (!slideId || slideId === 0) {
            captureError(new Error('Error downloading slide: missing slide ID!'), {
              component: 'useFolderManagerActions',
              skipConsole: true,
            });
            dispatch(addNotification({ type: 'error', message: "Couldn't download the slide." }));
            break;
          }

          try {
            const blob = await downloadSlide({ slideId }).unwrap();

            if (blob) {
              const slideName = payload?.displayName || payload?.name || 'slide';
              downloadBlobFile(blob, `${slideName}.pptx`);
              onActionSuccess?.(FolderManagerActionTypes.SLIDE_DOWNLOAD);
            }
          } catch (error) {
            captureError(error, { message: 'Failed to download slide', component: 'useFolderManagerActions' });
            dispatch(addNotification({ type: 'error', message: 'Slide download failed. Please try again.' }));
            onActionError?.(FolderManagerActionTypes.SLIDE_DOWNLOAD, error as Error);
          }
          break;
        }

        case FolderManagerActionTypes.COPY:
          const { id: assetToCopyId } = payload || {};

          if (!assetToCopyId || Number.isNaN(assetToCopyId)) {
            const msg = `Couldn't copy asset. Please try again.`;

            dispatch(
              addNotification({
                type: 'error',
                message: msg,
              }),
            );
            onActionError?.(FolderManagerActionTypes.COPY, new Error(msg));
            break;
          }

          if (!selectedFolderId || Number.isNaN(selectedFolderId)) {
            const msg = `Couldn't copy asset. Please try again.`;

            dispatch(
              addNotification({
                type: 'error',
                message: msg,
              }),
            );
            onActionError?.(FolderManagerActionTypes.COPY, new Error(msg));
            break;
          }

          try {
            await createAsset({ parentId: selectedFolderId!, data: { copy_of: assetToCopyId } }).unwrap();

            dispatch(
              addNotification({
                type: 'success',
                message: `Asset "${payload?.name}" has been successfully copied.`,
              }),
            );
            onActionSuccess?.(FolderManagerActionTypes.COPY);
          } catch (error: any) {
            const msg = `Couldn't copy "${payload?.name}". Please try again.`;
            dispatch(
              addNotification({
                type: 'error',
                message: msg,
              }),
            );
            onActionError?.(FolderManagerActionTypes.COPY, error);
          }
          break;

        case FolderManagerActionTypes.MANAGE_VERSIONS:
          const folderIdForVersions = selectedFolderId || payload?.parentId || payload?.folderId;

          if (!folderIdForVersions) {
            captureError(new Error('Could not handle manage versions. No folder ID available!'), {
              component: 'useFolderManagerActions',
              skipConsole: true,
            });
            return;
          }

          setConfirmationDialogState({
            opened: true,
            title: 'Manage Versions',
            content: <AssetVersionManager item={payload} selectedFolderId={folderIdForVersions} />,
            buttons: [
              {
                label: 'Close',
                callback: async () => {
                  setConfirmationDialogState(initialConfirmationDialogState);
                },
              },
            ],
          });
          break;

        case FolderManagerActionTypes.SET_AS_DEFAULT: {
          const themeId = payload?.presentationTheme?.ID;
          if (!themeId) {
            const msg = "Couldn't set the default theme — no theme was found for this file.";
            dispatch(addNotification({ type: 'error', message: msg }));
            onActionError?.(FolderManagerActionTypes.SET_AS_DEFAULT, new Error(msg));
            console.log('payload', payload);
            break;
          }
          try {
            await updatePresentationTheme({ themeId, data: { is_default: true } }).unwrap();
            dispatch(baseApi.util.invalidateTags([{ type: 'Folders' }]));
            onActionSuccess?.(FolderManagerActionTypes.SET_AS_DEFAULT);
          } catch (error: any) {
            captureError(error, { message: 'Error setting default theme', component: 'useFolderManagerActions' });
            onActionError?.(FolderManagerActionTypes.SET_AS_DEFAULT, error);
          }
          break;
        }

        case FolderManagerActionTypes.MANAGE_THEME: {
          if (!payload?.presentationTheme?.ID) {
            dispatch(
              addNotification({
                type: 'error',
                message: "Couldn't open Theme Manager — no theme data was found for this file.",
              }),
            );
            break;
          }
          setManageThemeDialogState({
            opened: true,
            themeItem: payload,
          });
          break;
        }

        case FolderManagerActionTypes.MOVE:
          const extractParentPptxItems = (slides: FolderManagerItem[]): FolderManagerItem[] => {
            const parentPptxMap = new Map<number, FolderManagerItem>();
            for (const slideItem of slides) {
              const parentPptx = (slideItem as any).parent;
              if (parentPptx?.id) {
                parentPptxMap.set(parentPptx.id, parentPptx);
              }
            }
            return Array.from(parentPptxMap.values());
          };

          // handle one item move
          if (payload?.item) {
            const moveItem = payload.item;
            // For slide items, move the parent pptx deck instead
            const itemsToMove =
              moveItem.type === FolderManagerItemTypes.SLIDE ? extractParentPptxItems([moveItem]) : [moveItem];

            if (itemsToMove.length > 0) {
              setMoveDialogState({
                opened: true,
                selectedItems: itemsToMove,
                rootFolder: rootFolder,
              });
            }
            return;
          }

          if (payload?.selectedItems) {
            setMoveDialogState({
              opened: true,
              selectedItems: payload.selectedItems,
              rootFolder: rootFolder,
            });
          }
          break;

        case FolderManagerActionTypes.UPLOAD: {
          const uploadTargetFolderId = payload?.targetFolderId ?? selectedFolderId;

          if (!uploadTargetFolderId) {
            dispatch(
              addNotification({
                type: 'error',
                message: `Couldn't upload file(s). No selected folder id provided!`,
              }),
            );
            onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('No selected folder id'));
            return;
          }

          if (!payload?.targetFolderId && !selectedFolderData) {
            dispatch(
              addNotification({
                type: 'error',
                message: `Couldn't upload file(s). No selected folder data!`,
              }),
            );
            onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('No selected folder data'));
            return;
          }

          if (payload?.files?.length > UPLOAD_FILES_LIMIT) {
            dispatch(
              addNotification({
                type: 'error',
                message: `Error uploading files. Please provide up to ${UPLOAD_FILES_LIMIT} files.`,
              }),
            );
            onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('Too many files'));
            return;
          }

          const item: FolderManagerItem = payload?.item;

          const ASSET_REPLACE_EXCLUDED_TYPES: string[] = [
            FolderManagerMimeTypes.SLIDE,
            FolderManagerMimeTypes.WORKSPACE,
            FolderManagerMimeTypes.FOLDER,
            FolderManagerMimeTypes.PRESENTATION,
          ];

          if (item && item.type && !ASSET_REPLACE_EXCLUDED_TYPES.includes(item.type)) {
            if (payload?.files?.length > 1) {
              dispatch(
                addNotification({
                  type: 'error',
                  message: 'Please drop only one file at a time when uploading a new version.',
                }),
              );
              return;
            }

            if (payload?.files?.[0]?.type !== item.type) {
              dispatch(
                addNotification({
                  type: 'error',
                  message: "Upload failed — the file type doesn't match the existing asset.",
                }),
              );
              return;
            }

            setConfirmationDialogState({
              opened: true,
              title: 'Upload new Asset Version',
              content: (
                <h4>
                  Upload "{payload?.files?.[0]?.name}" as a new version of "{item?.name}"?
                </h4>
              ),
              buttons: [
                {
                  label: 'Cancel',
                  variant: buttonVariants.clear,
                  callback: () => setConfirmationDialogState(initialConfirmationDialogState),
                },
                {
                  label: 'Upload',
                  callback: async () => {
                    setConfirmationDialogState(initialConfirmationDialogState);
                    const uploadResult = await uploadFiles({
                      folderId: selectedFolderId!,
                      files: payload?.files,
                      asset_id: item.id,
                    });
                    if (uploadResult?.notificationItemId && isSlidesFolder) {
                      setSlidesDialogState({
                        title: 'Slides',
                        opened: true,
                        jobId: uploadResult.notificationItemId,
                        filesToUpload: payload?.files,
                        folderId: selectedFolderId!,
                      });
                    }
                  },
                },
              ],
            });
            return;
          }

          const isSubFolderUpload = !!payload?.targetFolderId;

          if (isSubFolderUpload) {
            if (payload?.files?.length > 1) {
              onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('Only one file upload is supported'));
              return;
            }
            const uploadResult = await uploadFiles({ files: payload?.files, folderId: uploadTargetFolderId });
            if (uploadResult?.notificationItemId) {
              setSlidesDialogState({
                title: 'Slides',
                opened: true,
                jobId: uploadResult.notificationItemId,
                filesToUpload: payload?.files,
                folderId: uploadTargetFolderId,
              });
            } else {
              dispatch(addNotification({ type: 'error', message: "Upload couldn't be started. Please try again." }));
              onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('No notification ID returned'));
            }
            break;
          }

          let queries: any[] = [];
          let errors: string[] = [];

          for (const file of payload.files) {
            const { name: fileName, type: fileType } = file;

            if (
              Array.isArray(selectedFolderData.allowed_mime_types) &&
              !selectedFolderData.allowed_mime_types.includes(fileType)
            ) {
              errors.push(`Couldn't upload "${fileName}" — unsupported file type.`);
              continue;
            }

            if (isSlidesFolder || selectedFolderData.isThemeFolder) {
              // @todo: temporary until we implement correct protocol
              if (payload?.files?.length > 1) {
                // dispatch(addNotification({ type: 'error', message: 'Only one file upload is supported' }));
                onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('Only one file upload is supported'));
                return;
              }

              const uploadResult = await uploadFiles({ files: payload?.files, folderId: uploadTargetFolderId });

              if (uploadResult?.notificationItemId) {
                setSlidesDialogState({
                  title: isSlidesFolder ? 'Slides' : 'Layouts',
                  opened: true,
                  jobId: uploadResult.notificationItemId,
                  uploadRequestId: uploadResult.uploadRequestId,
                  filesToUpload: payload?.files,
                  folderId: uploadTargetFolderId,
                });
              } else {
                dispatch(addNotification({ type: 'error', message: "Upload couldn't be started. Please try again." }));
                onActionError?.(FolderManagerActionTypes.UPLOAD, new Error('No notification ID returned'));
              }

              return;
            }

            queries.push(uploadFiles({ files: payload?.files, folderId: uploadTargetFolderId }));
          }

          await Promise.all(queries);

          if (errors.length) {
            for (const error of errors) {
              dispatch(addNotification({ type: 'error', message: error }));
            }
          }
          onActionSuccess?.(FolderManagerActionTypes.UPLOAD);
          break;
        }

        case FolderManagerActionTypes.NAVIGATE:
          navigate(`/${payload.slug}`);
          onActionSuccess?.(FolderManagerActionTypes.NAVIGATE);
          break;

        default:
          console.warn('Unsupported action type triggered!', actionType);
          return;
      }
    },
    [
      customActionHandlers,
      slug,
      selectedFolderId,
      selectedFolderData,
      rootFolder,
      handleRestore,
      updateFolder,
      updateAsset,
      deleteFolder,
      deleteAsset,
      deletePresentation,
      generateZipBuildJob,
      downloadAssetById,
      uploadFiles,
      createAsset,
      updatePresentationTheme,
      dispatch,
      navigate,
      onActionSuccess,
      onActionError,
    ],
  );

  const handleMoveDialogDismiss = useCallback(() => {
    setMoveDialogState(initialMoveDialogState);
  }, []);

  const dialogs = (
    <>
      {confirmationDialogState.opened && (
        <ConfirmationDialog
          dialogState={confirmationDialogState}
          onDismiss={() => setConfirmationDialogState(initialConfirmationDialogState)}
          isLoading={false}
        />
      )}

      {shareDialogState.opened && (
        <FolderManagerShareDialog
          opened={shareDialogState.opened}
          selectedRows={shareDialogState.selectedItems}
          onDismiss={() => setShareDialogState(shareDialogInitialState)}
        />
      )}

      {renameDialogState?.opened && (
        <FolderManagerDialog
          isLoading={false}
          dialogState={renameDialogState}
          onDismiss={() => setRenameDialogState(initialRenameDialogState)}
        />
      )}

      {moveDialogState?.opened && (
        <FolderManagerMoveDialog dialogState={moveDialogState} onDismiss={handleMoveDialogDismiss} />
      )}

      {manageThemeDialogState?.opened && manageThemeDialogState.themeItem && (
        <ManageThemeDialog
          open={manageThemeDialogState.opened}
          onClose={() => setManageThemeDialogState(initialManageThemeDialogState)}
          themeData={manageThemeDialogState.themeItem}
          onSave={() => {
            setManageThemeDialogState(initialManageThemeDialogState);
            onActionSuccess?.(FolderManagerActionTypes.MANAGE_THEME);
          }}
        />
      )}

      {slidesDialogState?.opened && (
        <FolderManagerSlidesDialog
          dialogState={slidesDialogState}
          onDismiss={(data) => {
            // Invalidate RTK cache to refresh folder view with new slides
            const folderId = data?.folderId || slidesDialogState.folderId;
            if (folderId) {
              dispatch(baseApi.util.invalidateTags([{ type: 'Folders', id: `ASSETS_${folderId}` }]));
            }
            setSlidesDialogState(initialSlidesDialogState);
          }}
        />
      )}
    </>
  );

  return {
    handleAction,
    dialogs,
  };
};
